/**
 * Shared grid/list toggle + sort control for the Agents and Workflows pages, plus the hook that
 * persists a page's choice. Each page keeps its own filters; this only owns view and sort.
 */

import React, { useEffect, useState } from 'react';
import './CollectionToolbar.css';

export type CollectionView = 'grid' | 'list';
export type SortDirection = 'asc' | 'desc';

export interface SortOption<K extends string> {
  key: K;
  label: string;
  // Direction picked when switching to this key - e.g. dates and counts read best newest/largest first.
  defaultDirection: SortDirection;
}

export interface CollectionPrefs<K extends string> {
  view: CollectionView;
  sortKey: K;
  sortDirection: SortDirection;
}

/** View + sort state for one page, remembered per storage key. */
export function useCollectionPrefs<K extends string>(storageKey: string, defaults: CollectionPrefs<K>, validKeys: K[]) {
  const [prefs, setPrefs] = useState<CollectionPrefs<K>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved && validKeys.includes(saved.sortKey) && (saved.view === 'grid' || saved.view === 'list')) {
        return { ...defaults, ...saved };
      }
    } catch {
      // Storage unavailable or corrupt - fall back to defaults.
    }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(prefs));
    } catch {
      // Non-critical convenience - ignore.
    }
  }, [storageKey, prefs]);

  return [prefs, setPrefs] as const;
}

/** Toggle direction when re-picking the current key, otherwise switch key at its default direction. */
export function nextSort<K extends string>(
  prefs: CollectionPrefs<K>,
  key: K,
  options: SortOption<K>[]
): CollectionPrefs<K> {
  if (prefs.sortKey === key) {
    return { ...prefs, sortDirection: prefs.sortDirection === 'asc' ? 'desc' : 'asc' };
  }
  const option = options.find(o => o.key === key);
  return { ...prefs, sortKey: key, sortDirection: option?.defaultDirection ?? 'asc' };
}

export function compareValues(a: string | number | undefined, b: string | number | undefined, direction: SortDirection) {
  const sign = direction === 'asc' ? 1 : -1;
  if (a === undefined || a === '') return 1; // Missing values always sort last.
  if (b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * sign;
}

interface CollectionToolbarProps<K extends string> {
  prefs: CollectionPrefs<K>;
  onChange: (prefs: CollectionPrefs<K>) => void;
  sortOptions: SortOption<K>[];
  resultCount: number;
  totalCount: number;
  noun: string;
}

export function CollectionToolbar<K extends string>({
  prefs,
  onChange,
  sortOptions,
  resultCount,
  totalCount,
  noun,
}: CollectionToolbarProps<K>) {
  return (
    <div className="collection-toolbar">
      <span className="collection-toolbar__count">
        {resultCount === totalCount ? `${totalCount} ${noun}` : `${resultCount} of ${totalCount} ${noun}`}
      </span>

      <div className="collection-toolbar__controls">
        <label className="collection-toolbar__sort">
          Sort
          <select
            value={prefs.sortKey}
            onChange={(e) => onChange(nextSort({ ...prefs, sortKey: '' as K }, e.target.value as K, sortOptions))}
          >
            {sortOptions.map(o => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
          <button
            className="collection-toolbar__dir"
            onClick={() => onChange({ ...prefs, sortDirection: prefs.sortDirection === 'asc' ? 'desc' : 'asc' })}
            title={prefs.sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            aria-label="Toggle sort direction"
          >
            {prefs.sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </label>

        <div className="collection-toolbar__views" role="group" aria-label="View">
          <button
            className={prefs.view === 'grid' ? 'is-active' : ''}
            onClick={() => onChange({ ...prefs, view: 'grid' })}
            title="Grid view"
            aria-pressed={prefs.view === 'grid'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="2" width="5" height="5" rx="1" />
              <rect x="9" y="2" width="5" height="5" rx="1" />
              <rect x="2" y="9" width="5" height="5" rx="1" />
              <rect x="9" y="9" width="5" height="5" rx="1" />
            </svg>
          </button>
          <button
            className={prefs.view === 'list' ? 'is-active' : ''}
            onClick={() => onChange({ ...prefs, view: 'list' })}
            title="List view"
            aria-pressed={prefs.view === 'list'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface SortableHeaderProps<K extends string> {
  label: string;
  sortKey?: K;
  prefs: CollectionPrefs<K>;
  sortOptions: SortOption<K>[];
  onChange: (prefs: CollectionPrefs<K>) => void;
  className?: string;
}

/** A list-view column header; clicking a sortable one sorts by it (again to flip direction). */
export function SortableHeader<K extends string>({ label, sortKey, prefs, sortOptions, onChange, className }: SortableHeaderProps<K>) {
  if (!sortKey) return <th className={className}>{label}</th>;
  const active = prefs.sortKey === sortKey;
  return (
    <th className={className} aria-sort={active ? (prefs.sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        className={`collection-table__sort ${active ? 'is-active' : ''}`}
        onClick={() => onChange(nextSort(prefs, sortKey, sortOptions))}
      >
        {label}
        <span className="collection-table__arrow">{active ? (prefs.sortDirection === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

/** Stop a row action button's click from also opening the row. */
export const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();
